import { useState, useMemo, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import axios from "axios";
import { numeroPerExcel, scaricaExcel, FMT_EURO, FMT_PERC } from "../exportExcel";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "../Ricavi/Ricavi.css";
import "./Clienti.css";
import "../../HrGenerale/TrendKpi/TrendKpi.css"; // M/T/A toggle styles
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const TOP_N_BAR = 10;
const TOP_N_PIVOT = 15;

const TRIMESTRI = [1, 2, 3, 4];
const MESI_LABEL = ["", "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const meseToTrimestre = { 1: 1, 2: 1, 3: 1, 4: 2, 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4, 11: 4, 12: 4 };

// Helper YoY: pattern allineato a Ricavi.js / TrendKpi.js
const calcYoY = (curr, prev) => {
    if (curr == null || prev == null || prev === 0) return null;
    return Math.round(((curr - prev) / Math.abs(prev)) * 1000) / 10;
};

// Da rows aggregati per (anno, mese, cliente), produce mappa cliente →
// {byAnnoMese, byAnnoTrim, byAnno, totale} per tutti gli anni presenti.
const aggregaPerCliente = (rows) => {
    const byCliente = {};
    rows.forEach(r => {
        if (!byCliente[r.cliente]) byCliente[r.cliente] = { byAnnoMese: {}, byAnnoTrim: {}, byAnno: {}, totale: 0 };
        const c = byCliente[r.cliente];
        const ric = Number(r.ricavi) || 0;
        const a = r.anno;
        if (!c.byAnnoMese[a]) c.byAnnoMese[a] = {};
        if (!c.byAnnoTrim[a]) c.byAnnoTrim[a] = {};
        c.byAnnoMese[a][r.mese] = (c.byAnnoMese[a][r.mese] || 0) + ric;
        const t = meseToTrimestre[r.mese];
        c.byAnnoTrim[a][t] = (c.byAnnoTrim[a][t] || 0) + ric;
        c.byAnno[a] = (c.byAnno[a] || 0) + ric;
        c.totale += ric;
    });
    return byCliente;
};

// Stessa shape di aggregaPerCliente ma sommando tutti i clienti in un'unica entry "totale".
const aggregaTotale = (rows) => {
    const tot = { byAnnoMese: {}, byAnnoTrim: {}, byAnno: {}, totale: 0 };
    rows.forEach(r => {
        const ric = Number(r.ricavi) || 0;
        const a = r.anno;
        if (!tot.byAnnoMese[a]) tot.byAnnoMese[a] = {};
        if (!tot.byAnnoTrim[a]) tot.byAnnoTrim[a] = {};
        tot.byAnnoMese[a][r.mese] = (tot.byAnnoMese[a][r.mese] || 0) + ric;
        const t = meseToTrimestre[r.mese];
        tot.byAnnoTrim[a][t] = (tot.byAnnoTrim[a][t] || 0) + ric;
        tot.byAnno[a] = (tot.byAnno[a] || 0) + ric;
        tot.totale += ric;
    });
    return tot;
};

// Costruisce le righe pivot multi-anno, una per cliente. Per ogni anno A in `anni` confronta
// con A-1 da rowsYoY (gia' fetchato shiftando il range di -1 anno).
// Output: { cliente, byAnnoMese, byAnnoMeseYoy, byAnnoTrim, byAnnoTrimYoy, byAnno, byAnnoYoy, totale, totaleYoy }
const costruisciPivot = (rowsY, rowsYoY, anni) => {
    if (!anni || anni.length === 0) return [];
    const dataY = aggregaPerCliente(rowsY);
    const dataYoY = aggregaPerCliente(rowsYoY);
    const allClienti = new Set([...Object.keys(dataY), ...Object.keys(dataYoY)]);
    const result = [];
    allClienti.forEach(cliente => {
        const y = dataY[cliente] || { byAnnoMese: {}, byAnnoTrim: {}, byAnno: {}, totale: 0 };
        const yago = dataYoY[cliente] || { byAnnoMese: {}, byAnnoTrim: {}, byAnno: {}, totale: 0 };
        const byAnnoMeseYoy = {}, byAnnoTrimYoy = {}, byAnnoYoy = {};
        anni.forEach(a => {
            const ya = a - 1;
            byAnnoMeseYoy[a] = {};
            for (let m = 1; m <= 12; m++) byAnnoMeseYoy[a][m] = calcYoY(y.byAnnoMese[a]?.[m], yago.byAnnoMese[ya]?.[m]);
            byAnnoTrimYoy[a] = {};
            for (let t = 1; t <= 4; t++) byAnnoTrimYoy[a][t] = calcYoY(y.byAnnoTrim[a]?.[t], yago.byAnnoTrim[ya]?.[t]);
            byAnnoYoy[a] = calcYoY(y.byAnno[a], yago.byAnno[ya]);
        });
        result.push({
            cliente,
            byAnnoMese: y.byAnnoMese,
            byAnnoMeseYoy,
            byAnnoTrim: y.byAnnoTrim,
            byAnnoTrimYoy,
            byAnno: y.byAnno,
            byAnnoYoy,
            totale: y.totale,
            totaleYoy: calcYoY(y.totale, yago.totale),
        });
    });
    result.sort((a, b) => (b.totale || 0) - (a.totale || 0));
    return result;
};

// Sotto 1Mln formatta in K (interi). Da 1Mln in su passa a "X,X Mln €" (1 decimale).
const fmtEuroK = (v) => {
    if (v == null) return "";
    if (v === 0) return "0K €";
    if (Math.abs(v) >= 1_000_000) {
        return `${(v / 1_000_000).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}Mln €`;
    }
    const k = v / 1000;
    return `${Math.round(k).toLocaleString("it-IT")}K €`;
};

const fmtPerc = (v) => {
    if (v == null) return "";
    return `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const fmtEuroCompact = (v) => {
    if (v == null) return "—";
    if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toLocaleString("it-IT", { maximumFractionDigits: 1 }) + "Mln €";
    if (Math.abs(v) >= 1_000) return (v / 1_000).toLocaleString("it-IT", { maximumFractionDigits: 0 }) + "K €";
    return v.toLocaleString("it-IT", { maximumFractionDigits: 0 }) + " €";
};

// Le colonne data_fattura/competence_*_date arrivano come text dal DWH; possono valere "false".
// Ritorna stringa vuota in quel caso.
const fmtDateText = (v) => {
    if (v == null || v === "" || v === "false") return "";
    return v;
};

// Allineato a fmtEuroK: un valore e' "visivamente 0" quando viene mostrato come "0K €".
// Succede per |v| < 500 (Math.round(v/1000) = 0) e per v == null/0.
const isDisplayedZero = (v) => {
    if (v == null) return true;
    if (Math.abs(v) >= 1_000_000) return false;
    return Math.round(v / 1000) === 0;
};

const FullscreenIcon = ({ active }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {active ? (
            <>
                <polyline points="4 14 10 14 10 20"></polyline>
                <polyline points="20 10 14 10 14 4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        ) : (
            <>
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        )}
    </svg>
);

const ViewTypeToggle = ({ value, onChange }) => (
    <>
        <button type="button" className={`oper-hr-table-view-btn ${value === "mese" ? "active" : ""}`} onClick={() => onChange("mese")} title="Vista Mensile"><span>M</span></button>
        <button type="button" className={`oper-hr-table-view-btn ${value === "trimestre" ? "active" : ""}`} onClick={() => onChange("trimestre")} title="Vista Trimestrale"><span>T</span></button>
        <button type="button" className={`oper-hr-table-view-btn ${value === "anno" ? "active" : ""}`} onClick={() => onChange("anno")} title="Vista Annuale"><span>A</span></button>
    </>
);

const Clienti = ({ filters }) => {
    const [isFullscreenBar, setIsFullscreenBar] = useState(false);
    const [isFullscreenDocs, setIsFullscreenDocs] = useState(false);
    const [isFullscreenPivot, setIsFullscreenPivot] = useState(false);

    const [tableViewType, setTableViewType] = useState("trimestre");

    // Stato dati (aggancio backend dwh.analitica_sede_sett_comm)
    const [clientiBar, setClientiBar] = useState([]);
    const [documenti, setDocumenti] = useState([]);
    const [pivotRows, setPivotRows] = useState([]);
    const [pivotRowsYoY, setPivotRowsYoY] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") {
                setIsFullscreenBar(false);
                setIsFullscreenDocs(false);
                setIsFullscreenPivot(false);
            }
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, []);

    // ── Fetch dati: bar chart Ricavi per Cliente + tabella Documenti (in parallelo) ──
    useEffect(() => {
        if (!filters?.fromDate || !filters?.toDate) return;
        const controller = new AbortController();
        let cancelled = false;

        const buildParams = () => {
            const settoreCsv = (filters.settore || []).map(s => s.value).join(",");
            const commessaCsv = (filters.commessa || []).map(s => s.value).join(",");
            const clientiCsv = (filters.clienti || []).map(s => s.value).join(",");
            const voceAnaliticaCsv = (filters.voceAnalitica || []).map(s => s.value).join(",");
            const sedeCsv = (filters.sede || []).map(s => s.value).join(",");
            const p = { from: filters.fromDate, to: filters.toDate };
            if (settoreCsv) p.settore = settoreCsv;
            if (commessaCsv) p.commessa = commessaCsv;
            if (clientiCsv) p.cliente_fornitore = clientiCsv;
            if (voceAnaliticaCsv) p.voce_analitica = voceAnaliticaCsv;
            if (sedeCsv) p.sede = sedeCsv;
            return p;
        };

        (async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token");
                const config = {
                    params: buildParams(),
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                };
                const [resBar, resDocs, resPivot] = await Promise.all([
                    axios.get(`${API_URL}/admin/clienti/bar-clienti`, config),
                    axios.get(`${API_URL}/admin/clienti/documenti`, config),
                    axios.get(`${API_URL}/admin/clienti/pivot-trend`, config),
                ]);
                if (cancelled) return;
                setClientiBar(Array.isArray(resBar.data?.rows) ? resBar.data.rows : []);
                setDocumenti(Array.isArray(resDocs.data?.rows) ? resDocs.data.rows : []);
                setPivotRows(Array.isArray(resPivot.data?.rows) ? resPivot.data.rows : []);
                setPivotRowsYoY(Array.isArray(resPivot.data?.rowsYoY) ? resPivot.data.rowsYoY : []);
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                if (cancelled) return;
                console.error("Errore caricamento Clienti:", err);
                setClientiBar([]);
                setDocumenti([]);
                setPivotRows([]);
                setPivotRowsYoY([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, [filters]);

    // ── Bar chart ──
    // Default (non-fullscreen): solo top N, no scroll.
    // Fullscreen: tutti i clienti con dataZoom slider+inside per lo scroll verticale.
    // Sort DESC + yAxis inverse → i piu' alti in cima.
    const barOptions = useMemo(() => {
        const sortedAll = [...clientiBar].sort((a, b) => b.ricavi - a.ricavi);
        const total = sortedAll.length;
        const visible = Math.min(TOP_N_BAR, total);
        const sorted = isFullscreenBar ? sortedAll : sortedAll.slice(0, visible);
        return {
            grid: { left: 140, right: isFullscreenBar ? 90 : 60, top: 20, bottom: 30, containLabel: false },
            xAxis: {
                type: "value",
                axisLabel: { formatter: (v) => fmtEuroK(v), fontSize: 11, color: "#64748b" },
                splitLine: { lineStyle: { color: "#f1f5f9" } },
            },
            yAxis: {
                type: "category",
                inverse: true,
                data: sorted.map(d => d.cliente),
                axisLabel: { fontSize: 11, color: "#0f172a" },
                axisLine: { show: false },
                axisTick: { show: false },
            },
            dataZoom: isFullscreenBar && total > visible ? [
                {
                    type: "slider",
                    yAxisIndex: 0,
                    startValue: 0,
                    endValue: visible - 1,
                    showDetail: false,
                    width: 14,
                    right: 6,
                    brushSelect: false,
                },
                {
                    type: "inside",
                    yAxisIndex: 0,
                    startValue: 0,
                    endValue: visible - 1,
                    zoomOnMouseWheel: false,
                    moveOnMouseWheel: true,
                },
            ] : undefined,
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
                formatter: (params) => `<strong>${params[0].name}</strong><br/>Ricavi: ${fmtEuroCompact(params[0].value)}`,
            },
            series: [{
                type: "bar",
                data: sorted.map(d => d.ricavi),
                itemStyle: {
                    color: {
                        type: "linear", x: 0, y: 0, x2: 1, y2: 0,
                        colorStops: [
                            { offset: 0, color: "#86efac" },
                            { offset: 1, color: "#22c55e" },
                        ],
                    },
                    borderRadius: [0, 4, 4, 0],
                },
                barWidth: 22,
                label: {
                    show: true, position: "right",
                    formatter: (p) => fmtEuroK(p.value),
                    fontSize: 11, color: "#334155", fontWeight: 600,
                },
            }],
        };
    }, [clientiBar, isFullscreenBar]);

    // Documenti con ricavi != 0 lato display (esclude anche valori troppo piccoli che,
    // formattati in K, verrebbero mostrati come "0K €").
    const documentiVisibili = useMemo(
        () => documenti.filter(d => !isDisplayedZero(Number(d.ricavi))),
        [documenti]
    );

    // ── Pivot trend Ricavi per Cliente (multi-anno) ──
    // Mostra tutti gli anni presenti nel periodo selezionato in colonne separate, piu' una
    // colonna "Totale" cross-anno. Per ogni anno A si confronta YoY con A-1 da rowsYoY.
    const anniPivot = useMemo(() => {
        const set = new Set();
        pivotRows.forEach(r => set.add(r.anno));
        return Array.from(set).sort((a, b) => a - b);
    }, [pivotRows]);

    // Mese massimo "arrivato" per ogni anno: 12 per anni storici, mese corrente per l'anno
    // in corso, 0 per anni futuri. I mesi/trim oltre maxMese non vengono renderizzati.
    const maxMeseByAnno = useMemo(() => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;
        const map = {};
        anniPivot.forEach(a => {
            if (a < currentYear) map[a] = 12;
            else if (a === currentYear) map[a] = currentMonth;
            else map[a] = 0;
        });
        return map;
    }, [anniPivot]);

    const trimVisibili = (a) => {
        const max = maxMeseByAnno[a] ?? 12;
        return TRIMESTRI.filter(t => (t - 1) * 3 + 1 <= max);
    };
    const mesiVisibili = (a, t) => {
        const max = maxMeseByAnno[a] ?? 12;
        const start = (t - 1) * 3 + 1;
        return [start, start + 1, start + 2].filter(m => m <= max);
    };
    const colSpanAnnoMese = (a) =>
        trimVisibili(a).reduce((acc, t) => acc + mesiVisibili(a, t).length * 2 + 2, 0);
    const colSpanAnnoTrim = (a) =>
        trimVisibili(a).length * 2 + 2;

    const pivotPerCliente = useMemo(
        () => costruisciPivot(pivotRows, pivotRowsYoY, anniPivot),
        [pivotRows, pivotRowsYoY, anniPivot]
    );

    const topPivot = useMemo(() => pivotPerCliente.slice(0, TOP_N_PIVOT), [pivotPerCliente]);

    // Totale: somma di TUTTI i clienti del periodo (non solo i top mostrati), cross-anno.
    const totaleRiga = useMemo(() => {
        if (anniPivot.length === 0) return null;
        const y = aggregaTotale(pivotRows);
        const yago = aggregaTotale(pivotRowsYoY);
        const byAnnoMeseYoy = {}, byAnnoTrimYoy = {}, byAnnoYoy = {};
        anniPivot.forEach(a => {
            const ya = a - 1;
            byAnnoMeseYoy[a] = {};
            for (let m = 1; m <= 12; m++) byAnnoMeseYoy[a][m] = calcYoY(y.byAnnoMese[a]?.[m], yago.byAnnoMese[ya]?.[m]);
            byAnnoTrimYoy[a] = {};
            for (let t = 1; t <= 4; t++) byAnnoTrimYoy[a][t] = calcYoY(y.byAnnoTrim[a]?.[t], yago.byAnnoTrim[ya]?.[t]);
            byAnnoYoy[a] = calcYoY(y.byAnno[a], yago.byAnno[ya]);
        });
        return {
            byAnnoMese: y.byAnnoMese,
            byAnnoMeseYoy,
            byAnnoTrim: y.byAnnoTrim,
            byAnnoTrimYoy,
            byAnno: y.byAnno,
            byAnnoYoy,
            totale: y.totale,
            totaleYoy: calcYoY(y.totale, yago.totale),
        };
    }, [pivotRows, pivotRowsYoY, anniPivot]);

    // ── Render helper YoY ──
    const renderYoy = (v, key) => {
        if (v == null) return <td key={key}></td>;
        const cls = v < 0 ? "admin-ricavi-cell-negativo" : "admin-ricavi-cell-positivo";
        return <td key={key} className={cls}>{fmtPerc(v)}</td>;
    };

    // ── Celle riga in base al viewType (loop su tutti gli anni in anniPivot + Totale cross-anno) ──
    const renderRowCells = (row) => {
        const cells = [];
        anniPivot.forEach(a => {
            if (tableViewType === "anno") {
                cells.push(<td key={`av-${a}`} className="admin-ricavi-td-totale">{row.byAnno?.[a] != null ? fmtEuroK(row.byAnno[a]) : ""}</td>);
                cells.push(renderYoy(row.byAnnoYoy?.[a], `ay-${a}`));
            } else if (tableViewType === "trimestre") {
                trimVisibili(a).forEach(t => {
                    cells.push(<td key={`v-${a}-${t}`}>{row.byAnnoTrim?.[a]?.[t] != null ? fmtEuroK(row.byAnnoTrim[a][t]) : ""}</td>);
                    cells.push(renderYoy(row.byAnnoTrimYoy?.[a]?.[t], `y-${a}-${t}`));
                });
                cells.push(<td key={`anno-v-${a}`} className="admin-ricavi-td-totale">{row.byAnno?.[a] != null ? fmtEuroK(row.byAnno[a]) : ""}</td>);
                cells.push(renderYoy(row.byAnnoYoy?.[a], `anno-y-${a}`));
            } else {
                // mese: per ogni trim visibile -> mesi visibili + totale trim
                trimVisibili(a).forEach(t => {
                    mesiVisibili(a, t).forEach(m => {
                        cells.push(<td key={`mv-${a}-${m}`}>{row.byAnnoMese?.[a]?.[m] != null ? fmtEuroK(row.byAnnoMese[a][m]) : ""}</td>);
                        cells.push(renderYoy(row.byAnnoMeseYoy?.[a]?.[m], `my-${a}-${m}`));
                    });
                    cells.push(<td key={`tv-${a}-${t}`} className="admin-ricavi-td-totale">{row.byAnnoTrim?.[a]?.[t] != null ? fmtEuroK(row.byAnnoTrim[a][t]) : ""}</td>);
                    cells.push(renderYoy(row.byAnnoTrimYoy?.[a]?.[t], `ty-${a}-${t}`));
                });
            }
        });
        // Outer Totale (somma cross-anno)
        cells.push(<td key="outer-v" className="admin-ricavi-td-totale">{row.totale != null ? fmtEuroK(row.totale) : ""}</td>);
        cells.push(renderYoy(row.totaleYoy, "outer-y"));
        return cells;
    };

    // ── Headers dinamici (loop su anniPivot + Totale cross-anno) ──
    const renderTableHeaders = () => {
        if (tableViewType === "anno") {
            return (
                <>
                    <tr>
                        <th className="admin-ricavi-th-corner">Anno</th>
                        {anniPivot.map(a => <th key={a} colSpan={2}>{a}</th>)}
                        <th className="admin-ricavi-th-totale" colSpan={2}>Totale</th>
                    </tr>
                    <tr>
                        <th className="admin-ricavi-th-corner">Cliente</th>
                        {anniPivot.map(a => ([
                            <th key={`r-${a}`}>Ricavi</th>,
                            <th key={`y-${a}`}>Ricavi YoY</th>,
                        ]))}
                        <th className="admin-ricavi-th-totale">Ricavi</th>
                        <th className="admin-ricavi-th-totale">Ricavi YoY</th>
                    </tr>
                </>
            );
        }
        if (tableViewType === "trimestre") {
            return (
                <>
                    <tr>
                        <th className="admin-ricavi-th-corner">Anno</th>
                        {anniPivot.map(a => <th key={a} colSpan={colSpanAnnoTrim(a)}>{a}</th>)}
                        <th className="admin-ricavi-th-totale" colSpan={2} rowSpan={2}>Totale</th>
                    </tr>
                    <tr>
                        <th className="admin-ricavi-th-corner">Trimestre</th>
                        {anniPivot.map(a => ([
                            ...trimVisibili(a).map(t => <th key={`t-${a}-${t}`} colSpan={2}>Trim {t}</th>),
                            <th key={`tot-${a}`} colSpan={2} className="admin-ricavi-th-totale">Totale</th>,
                        ]))}
                    </tr>
                    <tr>
                        <th className="admin-ricavi-th-corner">Cliente</th>
                        {anniPivot.map(a => ([
                            ...trimVisibili(a).flatMap(t => [
                                <th key={`r-${a}-${t}`}>Ricavi</th>,
                                <th key={`y-${a}-${t}`}>Ricavi YoY</th>,
                            ]),
                            <th key={`tot-r-${a}`} className="admin-ricavi-th-totale">Ricavi</th>,
                            <th key={`tot-y-${a}`} className="admin-ricavi-th-totale">Ricavi YoY</th>,
                        ]))}
                        <th className="admin-ricavi-th-totale">Ricavi</th>
                        <th className="admin-ricavi-th-totale">Ricavi YoY</th>
                    </tr>
                </>
            );
        }
        // mese: 4 righe
        return (
            <>
                <tr>
                    <th className="admin-ricavi-th-corner">Anno</th>
                    {anniPivot.map(a => <th key={a} colSpan={colSpanAnnoMese(a)}>{a}</th>)}
                    <th className="admin-ricavi-th-totale" colSpan={2} rowSpan={3}>Totale</th>
                </tr>
                <tr>
                    <th className="admin-ricavi-th-corner">Trimestre</th>
                    {anniPivot.flatMap(a => trimVisibili(a).map(t => <th key={`tr-${a}-${t}`} colSpan={mesiVisibili(a, t).length * 2 + 2}>Trim {t}</th>))}
                </tr>
                <tr>
                    <th className="admin-ricavi-th-corner">Mese</th>
                    {anniPivot.flatMap(a => trimVisibili(a).flatMap(t => {
                        const cells = [];
                        mesiVisibili(a, t).forEach(m => {
                            cells.push(<th key={`m-${a}-${m}`} colSpan={2}>{MESI_LABEL[m]}</th>);
                        });
                        cells.push(<th key={`tot-${a}-${t}`} colSpan={2} className="admin-ricavi-th-totale">TOTALE</th>);
                        return cells;
                    }))}
                </tr>
                <tr>
                    <th className="admin-ricavi-th-corner">Cliente</th>
                    {anniPivot.flatMap(a => trimVisibili(a).flatMap(t => {
                        const cells = [];
                        mesiVisibili(a, t).forEach(m => {
                            cells.push(<th key={`rm-${a}-${m}`}>Ricavi</th>);
                            cells.push(<th key={`ym-${a}-${m}`}>Ricavi YoY</th>);
                        });
                        cells.push(<th key={`rt-${a}-${t}`} className="admin-ricavi-th-totale">Ricavi</th>);
                        cells.push(<th key={`yt-${a}-${t}`} className="admin-ricavi-th-totale">Ricavi YoY</th>);
                        return cells;
                    }))}
                    <th className="admin-ricavi-th-totale">Ricavi</th>
                    <th className="admin-ricavi-th-totale">Ricavi YoY</th>
                </tr>
            </>
        );
    };

    // ── Export Excel tabella documenti ──
    // Tabella piatta: al controller condiviso bastano { headers, rows }.
    const handleExportDocs = async () => {
        const headers = ["Id Doc", "Desc Documento", "Data Fattura", "Comp. Start", "Comp. End", "Settore", "Commessa", "Ricavi"];
        const rows = documentiVisibili.map(d => [
            d.numero_documento ?? "",
            d.des_conto ?? "",
            fmtDateText(d.data_fattura),
            fmtDateText(d.competence_start_date),
            fmtDateText(d.competence_end_date),
            d.des_settore ?? "",
            d.des_commessa ?? "",
            numeroPerExcel(d.ricavi),
        ]);
        const totaleRicavi = documentiVisibili.reduce((acc, d) => acc + (Number(d.ricavi) || 0), 0);
        rows.push(["Totale", "", "", "", "", "", "", numeroPerExcel(totaleRicavi)]);
        const numFmt = headers.map(h => h === "Ricavi" ? FMT_EURO : "");

        try {
            await scaricaExcel({
                endpoint: "admin/clienti/downloadExcel",
                tableName: "Documenti Clienti",
                datiDaStampare: { headers, rows, formattazione: { numFmt, negativiRossi: true, freeze: true } },
                filename: "documenti_clienti.xlsx",
            });
        } catch (err) {
            console.error("Errore durante il download del file:", err);
        }
    };

    // ── Export Excel tabella pivot (multi-anno, mese/trim futuri esclusi) ──
    // Header su due righe come a video: riga 1 il periodo (cella unita sulle due
    // metriche), riga 2 "Ricavi" / "YoY".
    const handleExportPivot = async () => {
        const headerGroups = [];
        anniPivot.forEach(a => {
            if (tableViewType === "anno") {
                headerGroups.push({ label: `${a}`, span: 2 });
            } else if (tableViewType === "trimestre") {
                trimVisibili(a).forEach(t => headerGroups.push({ label: `Trim ${t} ${a}`, span: 2 }));
                headerGroups.push({ label: `Totale ${a}`, span: 2 });
            } else {
                trimVisibili(a).forEach(t => {
                    mesiVisibili(a, t).forEach(m => headerGroups.push({ label: `${MESI_LABEL[m]} ${a}`, span: 2 }));
                    headerGroups.push({ label: `Trim ${t} Totale ${a}`, span: 2 });
                });
            }
        });
        headerGroups.push({ label: "Totale", span: 2 });

        const headers = ["Cliente"];
        headerGroups.forEach(() => headers.push("Ricavi", "YoY"));

        const rows = [];
        const appendRow = (row, label) => {
            const cells = [label];
            anniPivot.forEach(a => {
                if (tableViewType === "anno") {
                    cells.push(numeroPerExcel(row.byAnno?.[a]), numeroPerExcel(row.byAnnoYoy?.[a]));
                } else if (tableViewType === "trimestre") {
                    trimVisibili(a).forEach(t => cells.push(
                        numeroPerExcel(row.byAnnoTrim?.[a]?.[t]),
                        numeroPerExcel(row.byAnnoTrimYoy?.[a]?.[t])
                    ));
                    cells.push(numeroPerExcel(row.byAnno?.[a]), numeroPerExcel(row.byAnnoYoy?.[a]));
                } else {
                    trimVisibili(a).forEach(t => {
                        mesiVisibili(a, t).forEach(m => cells.push(
                            numeroPerExcel(row.byAnnoMese?.[a]?.[m]),
                            numeroPerExcel(row.byAnnoMeseYoy?.[a]?.[m])
                        ));
                        cells.push(
                            numeroPerExcel(row.byAnnoTrim?.[a]?.[t]),
                            numeroPerExcel(row.byAnnoTrimYoy?.[a]?.[t])
                        );
                    });
                }
            });
            cells.push(numeroPerExcel(row.totale), numeroPerExcel(row.totaleYoy));
            rows.push(cells);
        };

        topPivot.forEach(r => appendRow(r, r.cliente));
        if (totaleRiga) appendRow(totaleRiga, "Totale");

        const numFmt = headers.map(h => h === "Ricavi" ? FMT_EURO : h === "YoY" ? FMT_PERC : "");

        try {
            await scaricaExcel({
                endpoint: "admin/clienti/downloadExcel",
                tableName: "Trend Ricavi per Cliente",
                datiDaStampare: { headerGroups, headers, rows, formattazione: { numFmt, negativiRossi: true, freeze: true } },
                filename: `trend_ricavi_per_cliente_${tableViewType}.xlsx`,
            });
        } catch (err) {
            console.error("Errore durante il download del file:", err);
        }
    };

    if (loading) {
        return (
            <div className="admin-ricavi-container">
                <Lf23Loader />
            </div>
        );
    }

    return (
        <div className="admin-ricavi-container">
            {/* ═══ Row 1: Bar + Tabella documenti ═══ */}
            <div className="admin-ricavi-grid-2">
                {/* Bar chart: Ricavi Per Cliente */}
                <div className={`admin-ricavi-section${isFullscreenBar ? " fullscreen-trend" : ""}`}>
                    <RotateDevice active={isFullscreenBar} />
                    <div className="admin-ricavi-section-header">
                        <h3 className="admin-ricavi-section-title">Ricavi Per Cliente</h3>
                        <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenBar(!isFullscreenBar)} title={isFullscreenBar ? "Esci da tutto schermo" : "Tutto schermo"}>
                            <FullscreenIcon active={isFullscreenBar} />
                        </button>
                    </div>
                    <ReactECharts
                        option={barOptions}
                        style={{ height: isFullscreenBar ? "calc(100vh - 120px)" : "280px", width: "100%" }}
                        notMerge={true}
                    />
                </div>

                {/* Tabella documenti */}
                <div className={`admin-ricavi-table-section${isFullscreenDocs ? " fullscreen-trend" : ""}`}>
                    <RotateDevice active={isFullscreenDocs} />
                    <div className="admin-ricavi-table-header">
                        <h3 className="admin-ricavi-section-title">&nbsp;</h3>
                        <div className="admin-ricavi-actions">
                            <button className="admin-ricavi-download-btn" onClick={handleExportDocs} title="Esporta in Excel" disabled={documentiVisibili.length === 0}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Esporta
                            </button>
                            <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenDocs(!isFullscreenDocs)} title={isFullscreenDocs ? "Esci da tutto schermo" : "Tutto schermo"}>
                                <FullscreenIcon active={isFullscreenDocs} />
                            </button>
                        </div>
                    </div>
                    <div className="admin-ricavi-table-wrapper admin-clienti-docs-wrapper">
                        <table className="admin-ricavi-table admin-clienti-docs-table">
                            <thead>
                                <tr>
                                    <th>Id Doc</th>
                                    <th>Desc Documento</th>
                                    <th>Data Fattura</th>
                                    <th>Comp. Start</th>
                                    <th>Comp. End</th>
                                    <th>Settore</th>
                                    <th>Commessa</th>
                                    <th>Ricavi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {documentiVisibili.map((d, idx) => (
                                    <tr key={idx}>
                                        <td>{d.numero_documento ?? ""}</td>
                                        <td className="admin-clienti-td-desc">{d.des_conto ?? ""}</td>
                                        <td>{fmtDateText(d.data_fattura)}</td>
                                        <td>{fmtDateText(d.competence_start_date)}</td>
                                        <td>{fmtDateText(d.competence_end_date)}</td>
                                        <td>{d.des_settore ?? ""}</td>
                                        <td>{d.des_commessa ?? ""}</td>
                                        <td>{d.ricavi != null ? fmtEuroK(d.ricavi) : ""}</td>
                                    </tr>
                                ))}
                                <tr className="admin-ricavi-row-totale">
                                    <td colSpan={7} className="admin-clienti-td-totale-label">Totale</td>
                                    <td>{fmtEuroK(documentiVisibili.reduce((acc, d) => acc + (Number(d.ricavi) || 0), 0))}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ═══ Row 2: Pivot Trend Ricavi per Cliente (con M/T/A) ═══ */}
            <div className={`admin-ricavi-table-section${isFullscreenPivot ? " fullscreen-trend" : ""}`}>
                <RotateDevice active={isFullscreenPivot} />
                <div className="admin-ricavi-table-header">
                    <h3 className="admin-ricavi-section-title">Trend Ricavi per Cliente</h3>
                    <div className="oper-hr-table-view-type-container">
                        <ViewTypeToggle value={tableViewType} onChange={setTableViewType} />
                        <button className="admin-ricavi-download-btn" onClick={handleExportPivot} title="Esporta in Excel" disabled={topPivot.length === 0}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Esporta
                        </button>
                        <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenPivot(!isFullscreenPivot)} title={isFullscreenPivot ? "Esci da tutto schermo" : "Tutto schermo"}>
                            <FullscreenIcon active={isFullscreenPivot} />
                        </button>
                    </div>
                </div>

                <div className="admin-ricavi-table-wrapper">
                    <table className="admin-ricavi-table">
                        <thead>{renderTableHeaders()}</thead>
                        <tbody>
                            {topPivot.map((row, idx) => (
                                <tr key={idx}>
                                    <td className="admin-ricavi-cell-label">{row.cliente}</td>
                                    {renderRowCells(row)}
                                </tr>
                            ))}
                            {totaleRiga && (
                                <tr className="admin-ricavi-row-totale">
                                    <td className="admin-ricavi-cell-label">Totale</td>
                                    {renderRowCells(totaleRiga)}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Clienti;
